/**
 * Cashing out, from the fan's side.
 *
 * The hard part of this panel is not the button. It is answering "why can't I
 * have my money" in a way that does not feel like a runaround, because every
 * reason here is real and most of them are temporary. So each blocked state
 * gets its own sentence saying what is true and what changes it — never a
 * disabled button with no explanation, which is how a cashback product loses
 * someone's trust in one screen.
 *
 * The one deliberate omission: no progress bar toward the minimum. It turns
 * "you have $3.10" into "you have failed to reach $5", and the number is
 * already on screen.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  useAcceptTerms,
  usePayoutDestinations,
  usePayoutMethods,
  useRedemptionEligibility,
  useRedemptions,
  useRequestRedemption,
} from '../api/hooks.js';
import { Money, Stat } from './Money.js';
import { ErrorNote, Panel } from './shared.js';
import type {
  PayoutDestinationWire,
  PayoutMethodInfoWire,
  PayoutMethodWire,
  RedemptionEligibilityWire,
  RedemptionWire,
} from '../api/client.js';

/**
 * Fallback labels for the two rails.
 *
 * The server is the source of truth (`GET /v1/me/payout-methods`), so that this
 * client and the mobile one quote the same timings. These exist only so a row
 * in the history table has a word to show before that request lands — never to
 * make a promise about how long something takes, which is why they carry no
 * timing.
 */
const METHOD_FALLBACK: Record<PayoutMethodWire, string> = {
  gift_card: 'Gift card',
  bank_transfer: 'Bank transfer',
};

export function CashOutPanel() {
  const eligibility = useRedemptionEligibility(true);
  const redemptions = useRedemptions(true);
  const destinations = usePayoutDestinations(true);
  const methods = usePayoutMethods(true);
  const request = useRequestRedemption();

  const rows = useMemo(() => destinations.data ?? [], [destinations.data]);
  const [destinationId, setDestinationId] = useState<string | null>(null);

  /**
   * Follow the list rather than owning the choice.
   *
   * The selection is an id, and the list it points into arrives asynchronously
   * and changes when someone adds or removes a destination. Without this, a
   * removed destination stays selected and the cash out is submitted against an
   * id the server will answer 404 for — the fan sees "that destination does not
   * exist" about a row that is no longer on their screen.
   */
  useEffect(() => {
    if (rows.length === 0) {
      if (destinationId !== null) setDestinationId(null);
      return;
    }
    if (destinationId === null || !rows.some((row) => row.id === destinationId)) {
      setDestinationId(rows[0]!.id);
    }
  }, [rows, destinationId]);

  const selected = rows.find((row) => row.id === destinationId) ?? null;

  /**
   * One key per attempt, generated when the form is first shown and rotated
   * only on success.
   *
   * This is what makes a double-click or a retry-after-timeout safe: both send
   * the same key, and the server returns the redemption that key already
   * created rather than creating a second one. Generating it inside the submit
   * handler would defeat the entire mechanism.
   */
  const [attempt, setAttempt] = useState(() => crypto.randomUUID());

  const data = eligibility.data;
  const canSubmit =
    data !== undefined && data.blockedReason === null && selected !== null && !request.isPending;

  return (
    <Panel
      title="Cash out"
      subtitle="Your cashback, in your pocket."
    >
      {eligibility.isError && <ErrorNote error={eligibility.error} />}
      {request.isError && <ErrorNote error={request.error} />}

      <div className="stats">
        <Stat
          label="Ready to cash out"
          value={data?.redeemable}
          tone="positive"
          hint={
            data === undefined
              ? undefined
              : `From ${formatMoney(data.policy.minimumRedemption)}.`
          }
        />
        <Stat
          label="Still settling"
          value={data?.onHold}
          hint={
            data === undefined
              ? undefined
              : `Shops can refund for ${data.policy.holdDays} days.`
          }
        />
      </div>

      {data !== undefined && data.blockedReason !== null && <Blocked eligibility={data} />}

      {data !== undefined && data.blockedReason === null && (
        <form
          className="stack stack--tight"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) return;
            request.mutate(
              { idempotencyKey: attempt, destinationId: selected!.id },
              { onSuccess: () => setAttempt(crypto.randomUUID()) },
            );
          }}
        >
          {rows.length === 0 ? (
            /* No destination yet. Said as a next step rather than as a refusal:
               the money is theirs and nothing is wrong, there is simply nowhere
               to send it yet. The panel that fixes it is directly below. */
            <p className="empty">
              Add somewhere to send it below, and this becomes a one-tap cash out.
            </p>
          ) : (
            <fieldset className="choices">
              <legend className="field__label">Where should it go?</legend>
              {rows.map((option) => (
                <label key={option.id} className="choice">
                  <input
                    type="radio"
                    name="redemption-destination"
                    value={option.id}
                    checked={destinationId === option.id}
                    onChange={() => setDestinationId(option.id)}
                  />
                  <span className="choice__label">{option.displayName}</span>
                  <span className="choice__hint">{timingFor(option, methods.data)}</span>
                </label>
              ))}
            </fieldset>
          )}

          <button className="button" type="submit" disabled={!canSubmit}>
            {request.isPending ? 'Sending…' : `Cash out ${formatMoney(data.redeemable)}`}
          </button>
        </form>
      )}


      <History rows={redemptions.data} loading={redemptions.isPending} />
    </Panel>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Why not, and what changes it.
 *
 * `terms_acceptance_required` is the only one with something to do about it
 * here, so it is the only one carrying a button.
 */
function Blocked({ eligibility }: { eligibility: RedemptionEligibilityWire }) {
  const accept = useAcceptTerms();
  const terms = eligibility.termsToAccept;

  const message = useMemo(() => {
    switch (eligibility.blockedReason) {
      case 'below_minimum':
        return `You can cash out once you reach ${formatMoney(
          eligibility.policy.minimumRedemption,
        )}. Keep shopping through your creator's links and it will get there.`;
      case 'funds_on_hold':
        return `${formatMoney(
          eligibility.onHold,
        )} is still settling. Shops can refund a purchase for ${
          eligibility.policy.holdDays
        } days, so cashback waits that long before it can leave. Nothing is needed from you.`;
      case 'redemption_in_flight':
        return 'A cash out is already on its way. Once it lands you can start another.';
      case 'negative_balance':
        return 'One of your purchases was refunded, so your balance is below zero for now. It clears itself as you earn more — we will never take money from your card or account.';
      case 'terms_acceptance_required':
        return 'One quick thing before your first cash out: please accept the cashback terms.';
      default:
        return null;
    }
  }, [eligibility]);

  if (message === null) return null;

  return (
    <div className="pending-note">
      <span>{message}</span>

      {eligibility.blockedReason === 'terms_acceptance_required' && terms !== null && (
        <>
          {accept.isError && <ErrorNote error={accept.error} />}
          <div className="pending-note__actions">
            <a
              className="link"
              href="/terms"
              target="_blank"
              rel="noreferrer"
            >
              Read the terms (version {terms.version})
            </a>
            <button
              className="button"
              onClick={() => accept.mutate(terms.version)}
              disabled={accept.isPending}
            >
              {accept.isPending ? 'Saving…' : 'Accept and continue'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function History({ rows, loading }: { rows: RedemptionWire[] | undefined; loading: boolean }) {
  if (loading) return <p className="empty">Loading…</p>;
  if (rows === undefined || rows.length === 0) return null;

  /* Wrapped, like every other table here. Without it this one is 653px wide on
     a 375px screen and scrolls the whole *page* sideways rather than itself —
     which is how a phone ends up with a horizontally sliding dashboard. */
  return (
    <div className="table-scroll">
      <table className="table">
        <caption className="table__caption">Past cash outs</caption>
        <thead>
          <tr>
            <th scope="col">When</th>
            {/* The frozen display name, not the method. "Chase •••• 4305" is
                what someone is looking for when they scan this list; "Bank
                transfer" is a category they already know they chose. */}
            <th scope="col">Where</th>
            <th scope="col">Status</th>
            <th scope="col" className="table__right">
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{new Date(row.requestedAt).toLocaleDateString()}</td>
              <td>{row.destinationHint}</td>
              <td>
                <span className={`pill pill--${row.state}`}>
                  {STATE_LABEL[row.state] ?? row.state}
                </span>
                {/* A failure the fan can act on, rather than a status they cannot
                    interpret. The money is already back in their balance. */}
                {row.state === 'failed' && row.failureReason !== null && (
                  <span className="table__note">
                    {row.failureReason} — the money is back in your balance.
                  </span>
                )}
                {/* A return is a different event from a refusal, and reads
                    differently on purpose: it was sent, the bank handed it back,
                    and the account they picked is usually the thing to change. */}
                {row.state === 'returned' && (
                  <span className="table__note">
                    {row.returnReason ?? 'Your bank sent it back'} — the money is back in your
                    balance. Try a different destination.
                  </span>
                )}
              </td>
              <td className="table__right">
                <Money value={row.amount} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const STATE_LABEL: Record<string, string> = {
  requested: 'On its way',
  settled: 'Sent',
  failed: 'Did not go through',
  // Deliberately not "failed". It was sent and the bank handed it back, which
  // is a different thing to have happened and a different thing to fix.
  returned: 'Sent back by your bank',
};

/**
 * How long this destination takes, in the server's words.
 *
 * Falls back to the method's name alone rather than a guessed duration: an
 * invented "two to three days" that turns out to be wrong is worse than saying
 * nothing, because someone plans around it.
 */
function timingFor(
  destination: PayoutDestinationWire,
  methods: PayoutMethodInfoWire[] | undefined,
): string {
  const described = methods?.find((entry) => entry.method === destination.method);
  return described?.timing ?? METHOD_FALLBACK[destination.method];
}

/** Dollars for a sentence. Display only — never arithmetic. */
function formatMoney(value: { amountCents: number }): string {
  return `$${(value.amountCents / 100).toFixed(2)}`;
}
